import { memo, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import SiteCard from "./SiteCard";
import { useCharacterizationSites } from "./useCharacterizationSites";
import { useCharacterizationViewMode } from "./useCharacterizationViewMode";
import { InlineLoader } from "../../../components/loader/CharacterizationLoader";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/PageHeader";
import { CHARACTERIZATION_VIEW_ALL_REPORTS } from "../core/utils/characterizationViewMode";

const isMobileZone = (site) =>
  site?.positioning && String(site.positioning).toLowerCase() === "mobile";

const isLeftZone = (site) => {
  const pos = String(site?.positioning || "").toLowerCase();
  return pos === "gauche" || pos === "left";
};

/** Titre de groupe tel qu’en base (maquette : majuscules conservées). */
const formatGroupTitle = (text) => String(text || "Zone").trim();

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const zoneLabel = (site) => {
  if (!site?.positioning) return "Zone";
  const positioning = String(site.positioning);
  return `${positioning.charAt(0).toUpperCase()}${positioning.slice(1)}`;
};

const buildGroupedZones = (sites) => {
  const groupsByAddress = new Map();

  sites.forEach((site) => {
    if (site?.isSuperContainer && Array.isArray(site?.linkedContainers)) {
      const groupedZones = site.linkedContainers.filter(
        (zone) => !isMobileZone(zone),
      );
      const leftZone = groupedZones.find((zone) => isLeftZone(zone));
      const zonesForGroup = leftZone
        ? [leftZone]
        : groupedZones.length > 0
          ? [groupedZones[0]]
          : [];
      if (zonesForGroup.length === 0) return;

      const groupTitle = formatGroupTitle(
        site?.addressName || zonesForGroup[0]?.addressName || "Zone",
      );
      const groupKey = normalizeKey(groupTitle);
      const previous = groupsByAddress.get(groupKey) || {
        id: groupKey,
        title: groupTitle,
        zones: [],
      };
      previous.zones.push(...zonesForGroup);
      groupsByAddress.set(groupKey, previous);
      return;
    }

    const groupTitle = formatGroupTitle(site?.addressName || "Zone");
    const groupKey = normalizeKey(groupTitle);
    const previous = groupsByAddress.get(groupKey) || {
      id: groupKey,
      title: groupTitle,
      zones: [],
    };
    previous.zones.push(site);
    groupsByAddress.set(groupKey, previous);
  });

  return Array.from(groupsByAddress.values())
    .map((group) => ({
      ...group,
      zones: group.zones
        .filter((zone) => zone && zone._id)
        .sort((a, b) => zoneLabel(a).localeCompare(zoneLabel(b))),
    }))
    .filter((group) => group.zones.length > 0)
    .sort((a, b) => a.title.localeCompare(b.title));
};

const ZonesPanel = memo(({ sites, superContainers, isLoading, containers }) => {
  const { t } = useTranslation();
  const sitesWithoutMobile = useMemo(
    () => sites.filter((s) => !isMobileZone(s)),
    [sites],
  );
  const groupedZones = useMemo(
    () => buildGroupedZones(sitesWithoutMobile),
    [sitesWithoutMobile],
  );
  const [expandedGroupIds, setExpandedGroupIds] = useState({});

  useEffect(() => {
    setExpandedGroupIds((prevState) => {
      const nextState = {};
      groupedZones.forEach((group) => {
        nextState[group.id] =
          prevState[group.id] !== undefined ? prevState[group.id] : true;
      });
      const prevKeys = Object.keys(prevState);
      const nextKeys = Object.keys(nextState);
      const hasSameSize = prevKeys.length === nextKeys.length;
      const hasSameValues =
        hasSameSize &&
        nextKeys.every((key) => prevState[key] === nextState[key]);
      if (hasSameValues) {
        return prevState;
      }
      return nextState;
    });
  }, [groupedZones]);

  const toggleGroup = (groupId) => {
    setExpandedGroupIds((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const formatCount = (count) => {
    if (count <= 1) {
      return `${count} ${t("Zone")}`;
    }
    return `${count} zones`;
  };

  return (
    <div className="char-zones-panel">
      <div className="char-zones-panel-container">
        <h2 className="char-zones-panel-container-title">{t("Zones")}</h2>
        <p className="char-zones-panel-container-description">
          {t("Select a characterization zone to analyze")}
        </p>
        <div className="char-zones-panel-container-list">
          {isLoading && <InlineLoader height="200px" />}
          {!isLoading && sitesWithoutMobile.length === 0 && (
            <div className="char-text-14 char-text-secondary">
              {t("No zones available at the moment")}
            </div>
          )}
          {groupedZones.map((group) => {
            const isExpanded = expandedGroupIds[group.id];
            return (
              <div key={group.id} className="char-zones-group">
                <button
                  type="button"
                  className="char-zones-group-header"
                  onClick={() => toggleGroup(group.id)}
                >
                  <div className="char-zones-group-header-title">
                    <span className="char-zones-group-header-name mr-1">{`${group.title} - `}</span>
                    <span className="char-zones-group-header-suffix">
                      <span className="char-zones-group-header-count mr-2">
                        {formatCount(group.zones.length)}
                      </span>
                      <i
                        className={`fas fa-chevron-${isExpanded ? "up" : "down"} char-zones-group-header-chevron`}
                        aria-hidden
                      />
                    </span>
                  </div>
                </button>
                {isExpanded && (
                  <div className="char-zones-group-list">
                    {group.zones.map((entry) => (
                      <SiteCard
                        key={entry._id}
                        site={entry}
                        superContainers={superContainers}
                        containers={containers}
                        compact={true}
                        titleOverride={zoneLabel(entry)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

const CharacterizationPageHeader = ({ title }) => (
  <PageHeader title={title} tabs={[]} />
);

const CharacterizationZones = () => {
  const { t } = useTranslation();
  const { sites, superContainers, isLoading, containers } =
    useCharacterizationSites();
  return (
    <div className="settings-page-wrapper char-zones-page">
      <CharacterizationPageHeader title={t("Characterizations")} />
      <div className="settings-content-wrapper char-zones-page__content">
        <ZonesPanel
          sites={sites}
          superContainers={superContainers}
          containers={containers}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

const Characterization = () => {
  const { t } = useTranslation();
  const { mode, isLoading } = useCharacterizationViewMode();

  if (isLoading) {
    return (
      <div className="settings-page-wrapper char-zones-page">
        <CharacterizationPageHeader title={t("Characterizations")} />
        <div className="settings-content-wrapper char-zones-page__content">
          <InlineLoader height="200px" />
        </div>
      </div>
    );
  }

  if (mode === CHARACTERIZATION_VIEW_ALL_REPORTS) {
    return <Redirect to="/characterization/list" />;
  }

  return <CharacterizationZones />;
};
export default Characterization;
